/**
 * Avatar customization catalogue.
 *
 * Items are organised into "slots" (currently `hat` only, expandable to
 * glasses / suitColor in future iterations). Each item carries:
 *
 *   id        — unique
 *   labelKey  — i18n key under t.avatar.items.<key>
 *   slot      — 'hat'
 *   unlockXp  — minimum total XP needed to unlock
 *   emoji     — fallback hint for the picker
 *   render    — optional render function returning JSX painted on top of
 *               the mascot's head (centred on the 200×200 mascot viewBox
 *               at roughly y=50). Pure presentational, no props.
 *
 * The `none` item per slot is what every child starts with.
 */

// Tiny SVG overlays. Each sits within the same 200×200 viewBox the mascots
// use and is positioned over the mascot's head. Kept monochrome-friendly so
// they read on all three theme backgrounds.

function SunHat() {
  return (
    <g>
      <ellipse cx="100" cy="56" rx="56" ry="9" fill="#fde68a" stroke="#b45309" strokeWidth="2" />
      <ellipse cx="100" cy="46" rx="30" ry="14" fill="#fde68a" stroke="#b45309" strokeWidth="2" />
      <rect x="80" y="52" width="40" height="3" rx="1.5" fill="#ef4444" />
    </g>
  );
}

function SpaceHelmet() {
  return (
    <g>
      <circle cx="100" cy="48" r="22" fill="#e2e8f0" stroke="#475569" strokeWidth="2" opacity="0.85" />
      <path d="M85 42 Q100 30 115 42" stroke="#ffffff" strokeWidth="3" fill="none" opacity="0.9" />
      <circle cx="100" cy="28" r="4" fill="#fbbf24" />
      <line x1="100" y1="24" x2="100" y2="18" stroke="#94a3b8" strokeWidth="2" />
    </g>
  );
}

function WizardCap() {
  return (
    <g>
      <path d="M70 60 L100 14 L130 60 Z" fill="#4f46e5" stroke="#1e1b4b" strokeWidth="2" />
      <path d="M70 58 L130 58 L126 64 L74 64 Z" fill="#fbbf24" stroke="#b45309" strokeWidth="1.5" />
      <circle cx="100" cy="22" r="3" fill="#fcd34d" />
      <path d="M86 38 L92 36 L88 44 Z" fill="#fde68a" />
    </g>
  );
}

function Crown() {
  return (
    <g>
      <path d="M70 58 L78 36 L90 50 L100 30 L110 50 L122 36 L130 58 Z" fill="#fcd34d" stroke="#b45309" strokeWidth="2" />
      <rect x="70" y="56" width="60" height="6" rx="2" fill="#fbbf24" stroke="#b45309" strokeWidth="2" />
      <circle cx="78" cy="40" r="3" fill="#ef4444" />
      <circle cx="100" cy="34" r="3" fill="#22d3ee" />
      <circle cx="122" cy="40" r="3" fill="#10b981" />
    </g>
  );
}

function CaptainCap() {
  return (
    <g>
      <ellipse cx="100" cy="60" rx="44" ry="6" fill="#ffffff" stroke="#0c4a6e" strokeWidth="2" />
      <path d="M62 60 Q100 36 138 60 L138 62 L62 62 Z" fill="#0c4a6e" />
      <rect x="78" y="52" width="44" height="6" rx="2" fill="#ffffff" />
      <circle cx="100" cy="44" r="5" fill="#fbbf24" stroke="#b45309" strokeWidth="1.5" />
    </g>
  );
}

// ─── Phase 2 additions (10+ new hats with combined unlock gates) ────────

function DinosaurCap() {
  return (
    <g>
      <path d="M58 58 Q100 18 142 58 L142 62 L58 62 Z" fill="#22c55e" stroke="#14532d" strokeWidth="2" />
      <path d="M70 32 L78 18 L86 32 Z" fill="#16a34a" stroke="#14532d" strokeWidth="1.5" />
      <path d="M90 26 L100 12 L110 26 Z" fill="#16a34a" stroke="#14532d" strokeWidth="1.5" />
      <path d="M114 32 L122 18 L130 32 Z" fill="#16a34a" stroke="#14532d" strokeWidth="1.5" />
      <circle cx="88" cy="50" r="2.5" fill="#fde68a" />
      <circle cx="112" cy="50" r="2.5" fill="#fde68a" />
    </g>
  );
}

function PirateHat() {
  return (
    <g>
      <path d="M58 60 Q100 18 142 60 L142 64 L58 64 Z" fill="#1f2937" stroke="#000" strokeWidth="2" />
      <path d="M58 60 Q100 34 142 60" fill="none" stroke="#facc15" strokeWidth="2" />
      <circle cx="100" cy="38" r="6" fill="#f3f4f6" stroke="#000" strokeWidth="1.5" />
      <circle cx="98" cy="36" r="1" fill="#1f2937" />
      <circle cx="102" cy="36" r="1" fill="#1f2937" />
      <path d="M97 41 L100 44 L103 41" stroke="#1f2937" strokeWidth="1" fill="none" />
    </g>
  );
}

function KnightHelmet() {
  return (
    <g>
      <path d="M70 60 L70 30 Q100 14 130 30 L130 60 Z" fill="#94a3b8" stroke="#334155" strokeWidth="2" />
      <rect x="80" y="38" width="40" height="4" fill="#1f2937" />
      <path d="M100 14 L100 8" stroke="#dc2626" strokeWidth="2" />
      <path d="M96 8 Q100 0 104 8 Q100 6 96 8 Z" fill="#dc2626" />
      <line x1="86" y1="50" x2="114" y2="50" stroke="#475569" strokeWidth="1" />
    </g>
  );
}

function ArcherCap() {
  return (
    <g>
      <path d="M62 60 Q100 22 138 60 L138 62 L62 62 Z" fill="#15803d" stroke="#14532d" strokeWidth="2" />
      <path d="M118 50 Q132 30 142 28 Q138 38 132 50 Z" fill="#facc15" stroke="#a16207" strokeWidth="1.5" />
      <path d="M118 50 Q126 38 132 28" stroke="#a16207" strokeWidth="1" fill="none" />
    </g>
  );
}

function DetectiveCap() {
  return (
    <g>
      <ellipse cx="100" cy="60" rx="48" ry="6" fill="#92400e" stroke="#451a03" strokeWidth="2" />
      <path d="M64 60 Q100 24 136 60 L136 62 L64 62 Z" fill="#a16207" stroke="#451a03" strokeWidth="2" />
      <path d="M64 60 Q100 36 136 60" stroke="#451a03" strokeWidth="0.8" fill="none" />
      <path d="M82 38 L82 54 L78 60 M118 38 L118 54 L122 60" stroke="#451a03" strokeWidth="0.8" fill="none" />
    </g>
  );
}

function PharaohHeaddress() {
  return (
    <g>
      <path d="M60 60 L60 32 Q100 14 140 32 L140 60 Z" fill="#fde68a" stroke="#a16207" strokeWidth="2" />
      <line x1="72" y1="30" x2="72" y2="60" stroke="#1d4ed8" strokeWidth="2" />
      <line x1="88" y1="22" x2="88" y2="60" stroke="#1d4ed8" strokeWidth="2" />
      <line x1="112" y1="22" x2="112" y2="60" stroke="#1d4ed8" strokeWidth="2" />
      <line x1="128" y1="30" x2="128" y2="60" stroke="#1d4ed8" strokeWidth="2" />
      <path d="M94 22 Q100 14 106 22 L106 28 Q100 24 94 28 Z" fill="#dc2626" stroke="#7f1d1d" strokeWidth="1.5" />
    </g>
  );
}

function RobotHelmet() {
  return (
    <g>
      <rect x="68" y="22" width="64" height="38" rx="6" fill="#cbd5e1" stroke="#1e293b" strokeWidth="2" />
      <rect x="78" y="34" width="44" height="10" rx="2" fill="#0f172a" />
      <circle cx="90" cy="39" r="2" fill="#22d3ee" />
      <circle cx="110" cy="39" r="2" fill="#22d3ee" />
      <line x1="100" y1="22" x2="100" y2="12" stroke="#475569" strokeWidth="2" />
      <circle cx="100" cy="10" r="3" fill="#fbbf24" />
    </g>
  );
}

function OlympicLaurel() {
  return (
    <g>
      <path d="M58 56 Q70 36 82 36 Q86 44 78 50 Q74 56 58 60 Z" fill="#16a34a" stroke="#14532d" strokeWidth="1.5" />
      <path d="M142 56 Q130 36 118 36 Q114 44 122 50 Q126 56 142 60 Z" fill="#16a34a" stroke="#14532d" strokeWidth="1.5" />
      <path d="M68 48 Q74 44 80 46" stroke="#14532d" strokeWidth="1" fill="none" />
      <path d="M132 48 Q126 44 120 46" stroke="#14532d" strokeWidth="1" fill="none" />
      <circle cx="100" cy="42" r="4" fill="#fbbf24" stroke="#b45309" strokeWidth="1.5" />
    </g>
  );
}

function CosmicCaptainCap() {
  return (
    <g>
      <ellipse cx="100" cy="60" rx="46" ry="6" fill="#312e81" stroke="#1e1b4b" strokeWidth="2" />
      <path d="M62 60 Q100 30 138 60 L138 62 L62 62 Z" fill="#4338ca" stroke="#1e1b4b" strokeWidth="2" />
      <rect x="78" y="52" width="44" height="5" rx="2" fill="#fbbf24" />
      <text x="100" y="44" textAnchor="middle" fontSize="10" fill="#fde68a">★</text>
      <text x="86" y="48" textAnchor="middle" fontSize="6" fill="#fde68a">★</text>
      <text x="114" y="48" textAnchor="middle" fontSize="6" fill="#fde68a">★</text>
    </g>
  );
}

function DiademOfKnowledge() {
  return (
    <g>
      <path d="M64 58 Q70 28 76 56 Q90 30 100 54 Q110 30 124 56 Q130 28 136 58 Z" fill="#facc15" stroke="#b45309" strokeWidth="2" />
      <rect x="64" y="56" width="72" height="6" rx="2" fill="#eab308" stroke="#b45309" strokeWidth="2" />
      <circle cx="76" cy="40" r="3" fill="#ef4444" />
      <circle cx="100" cy="32" r="4" fill="#22d3ee" />
      <circle cx="124" cy="40" r="3" fill="#a855f7" />
    </g>
  );
}

function TreasureChestHat() {
  return (
    <g>
      <rect x="68" y="38" width="64" height="22" rx="4" fill="#a16207" stroke="#451a03" strokeWidth="2" />
      <path d="M68 38 Q100 18 132 38" fill="#92400e" stroke="#451a03" strokeWidth="2" />
      <rect x="92" y="44" width="16" height="14" rx="2" fill="#fbbf24" stroke="#92400e" strokeWidth="1.5" />
      <circle cx="100" cy="51" r="1.5" fill="#451a03" />
      <circle cx="80" cy="32" r="3" fill="#facc15" stroke="#b45309" strokeWidth="1" />
      <circle cx="120" cy="32" r="3" fill="#facc15" stroke="#b45309" strokeWidth="1" />
    </g>
  );
}

function TimeTravelerHelm() {
  return (
    <g>
      <ellipse cx="100" cy="44" rx="32" ry="24" fill="#1e293b" stroke="#0f172a" strokeWidth="2" />
      <path d="M76 42 Q100 30 124 42" stroke="#22d3ee" strokeWidth="2" fill="none" opacity="0.9" />
      <circle cx="100" cy="44" r="14" fill="#0f172a" stroke="#22d3ee" strokeWidth="2" />
      <line x1="100" y1="34" x2="100" y2="40" stroke="#22d3ee" strokeWidth="2" />
      <line x1="100" y1="48" x2="106" y2="52" stroke="#fbbf24" strokeWidth="2" />
      <line x1="100" y1="48" x2="94" y2="46" stroke="#f97316" strokeWidth="1.5" />
      <circle cx="100" cy="44" r="2" fill="#fbbf24" />
    </g>
  );
}

export const AVATAR_CATALOG = {
  hat: [
    // ── Pre-Phase-2 hats. ORDER + IDS MUST NOT CHANGE — they appear
    // before new hats so the picker keeps the kid's familiar layout intact.
    { id: 'none', labelKey: 'noHat', slot: 'hat', unlockXp: 0, emoji: '🚫', render: null },
    { id: 'sun_hat', labelKey: 'sunHat', slot: 'hat', unlockXp: 75, emoji: '👒', render: () => <SunHat /> },
    { id: 'space_helmet', labelKey: 'spaceHelmet', slot: 'hat', unlockXp: 200, emoji: '👨‍🚀', render: () => <SpaceHelmet /> },
    { id: 'captain_cap', labelKey: 'captainCap', slot: 'hat', unlockXp: 400, emoji: '🧑‍✈️', render: () => <CaptainCap /> },
    { id: 'wizard_cap', labelKey: 'wizardCap', slot: 'hat', unlockXp: 600, emoji: '🧙', render: () => <WizardCap /> },
    { id: 'crown', labelKey: 'crown', slot: 'hat', unlockXp: 1000, emoji: '👑', render: () => <Crown /> },

    // ── Phase 2 additions. Higher XP thresholds + badge prerequisites so
    // the kid has to engage with multiple parts of the app to unlock them.
    // `requiredBadges` is AND-ed against `unlockXp` — both must be satisfied.
    { id: 'dinosaur_cap', labelKey: 'dinosaurCap', slot: 'hat', unlockXp: 1500, emoji: '🦖', requiredBadges: ['quests_5'], render: () => <DinosaurCap /> },
    { id: 'pirate_hat', labelKey: 'pirateHat', slot: 'hat', unlockXp: 1800, emoji: '🏴‍☠️', requiredBadges: ['correct_streak_5'], render: () => <PirateHat /> },
    { id: 'knight_helmet', labelKey: 'knightHelmet', slot: 'hat', unlockXp: 2500, emoji: '🛡', requiredBadges: ['sessions_25'], render: () => <KnightHelmet /> },
    { id: 'archer_cap', labelKey: 'archerCap', slot: 'hat', unlockXp: 2800, emoji: '🏹', requiredBadges: ['correct_streak_10'], render: () => <ArcherCap /> },
    { id: 'detective_cap', labelKey: 'detectiveCap', slot: 'hat', unlockXp: 3000, emoji: '🕵', requiredBadges: ['find_mistake_50'], render: () => <DetectiveCap /> },
    { id: 'pharaoh_headdress', labelKey: 'pharaohHeaddress', slot: 'hat', unlockXp: 3500, emoji: '🪭', requiredBadges: ['trained_days_15'], render: () => <PharaohHeaddress /> },
    { id: 'robot_helmet', labelKey: 'robotHelmet', slot: 'hat', unlockXp: 5000, emoji: '🤖', requiredBadges: ['multiple_choice_50'], render: () => <RobotHelmet /> },
    { id: 'olympic_laurel', labelKey: 'olympicLaurel', slot: 'hat', unlockXp: 6000, emoji: '🌿', requiredBadges: ['trained_days_30'], render: () => <OlympicLaurel /> },
    { id: 'cosmic_captain_cap', labelKey: 'cosmicCaptainCap', slot: 'hat', unlockXp: 8000, emoji: '🚀', requiredBadges: ['sessions_100'], render: () => <CosmicCaptainCap /> },
    { id: 'diadem_of_knowledge', labelKey: 'diademOfKnowledge', slot: 'hat', unlockXp: 10000, emoji: '💎', requiredBadges: ['reviews_10', 'mastered_25'], render: () => <DiademOfKnowledge /> },
    { id: 'treasure_chest_hat', labelKey: 'treasureChestHat', slot: 'hat', unlockXp: 12000, emoji: '🪙', requiredBadges: ['quests_25', 'fill_blank_50'], render: () => <TreasureChestHat /> },
    { id: 'time_traveler_helm', labelKey: 'timeTravelerHelm', slot: 'hat', unlockXp: 15000, emoji: '⏳', requiredBadges: ['trained_days_30', 'reviews_50'], render: () => <TimeTravelerHelm /> },
  ],
};

export const AVATAR_SLOTS = ['hat'];

export const DEFAULT_AVATAR = {
  hat: 'none',
};
